import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { CITIES } from "@/data/citiesData";

export default function VillesDesserviesPage() {
  return (
    <div className="min-h-screen" data-testid="villes-page">
      <SEO
        title="Villes desservies par Mediastore Niort - Réparation Apple Deux-Sèvres & 80 km"
        description="Mediastore Niort intervient à Niort, Chauray, Aiffres, Saint-Maixent-l'École, Melle, Parthenay, Bressuire, Surgères, La Rochelle, Rochefort, Poitiers et toutes les villes dans un rayon de 80 km. Réparation Apple, iPhone, Mac, ordinateur. Spécialiste Apple certifié."
        keywords="villes desservies Mediastore, réparation Apple Deux-Sèvres, réparation iPhone Chauray, réparation Mac Saint-Maixent, réparation iPhone Parthenay, réparation Apple La Rochelle, réparation iPhone Poitiers, réparation iPhone Rochefort, réparation iPhone Fontenay-le-Comte, réparateur Apple proche 79, réparateur Apple Charente-Maritime, réparateur Apple Vienne, réparateur Apple Vendée"
        path="/villes-desservies"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Villes desservies", path: "/villes-desservies" },
        ]}
      />

      <section className="py-16 bg-gradient-to-br from-slate-900 to-red-900 text-white">
        <div className="container-custom text-center">
          <Badge className="bg-white/10 text-white border border-white/20 px-4 py-2 mb-6 hover:bg-white/10">
            <MapPin className="w-4 h-4 mr-1" />
            Zone d&apos;intervention 80 km
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Les villes que nous desservons
          </h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
            Mediastore Niort, spécialiste Apple certifié, intervient pour tous les habitants
            de la <strong className="text-white">Deux-Sèvres</strong>, de la
            <strong className="text-white"> Charente-Maritime</strong> nord, de la
            <strong className="text-white"> Vienne</strong> ouest et de la
            <strong className="text-white"> Vendée</strong> sud-est dans un rayon de
            <strong className="text-red-400"> 80 km autour de Niort 79000</strong>.
          </p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="container-custom max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {CITIES.length} villes desservies
            </h2>
            <p className="text-lg text-slate-600">
              Cliquez sur votre ville pour découvrir nos services locaux, distances et FAQ.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {CITIES.map((city) => (
              <Link
                key={city.slug}
                to={`/reparation-${city.slug}`}
                className="block group"
                data-testid={`city-link-${city.slug}`}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-red-200">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-slate-900 group-hover:text-red-600 transition-colors">
                          {city.name}
                        </h3>
                        <p className="text-sm text-slate-500">{city.postalCode}</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded font-medium">
                        {city.distanceKm} km
                      </span>
                      <span>{city.timeMinutes} min</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-10 max-w-3xl mx-auto">
            Et toutes les communes dans un rayon de 80 km autour de Niort 79000. Vous habitez une
            ville non listée ? Contactez-nous au <strong>05 49 05 74 90</strong> ou utilisez notre
            service <Link to="/click-collect" className="text-red-600 underline">Click &amp; Collect</Link>{" "}
            avec expédition Colissimo gratuite au-delà de 49 € de réparation.
          </p>
        </div>
      </section>

      <section className="py-12 bg-red-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Besoin d&apos;un diagnostic gratuit ?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+33549057490">
              <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 gap-2">
                <Phone className="h-5 w-5" />
                05 49 05 74 90
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
